import { ReactElement } from 'react';

import { useTheme } from '@mui/material';

import { Icons, Tooltip, Stack } from '@cast/design-system';
import { useAbility } from '@cast/shared-ability-data-access';
import { RADAR_NAV_TITLE } from '@cast/shared-trial-ui';
import { K8sProvider } from '@cast/shared-types';

import { OnboardClusterButton } from 'components/clickable-actions';

import { ClusterListEntity } from '../../features/dashboard/types';
import { useGetOrganizationClusterFeaturesAccess } from '../../hooks/useGetOrganizationClusterFeaturesAccess';

const FeatureIcon = ({
  title,
  children,
}: {
  title: string;
  children: ReactElement<any, any>;
}) => (
  <Tooltip title={title} arrow placement="top">
    {children}
  </Tooltip>
);

export const FeaturesCell = (cluster: ClusterListEntity) => {
  const ability = useAbility();

  const { getOrganizationClusterFeatures } =
    useGetOrganizationClusterFeaturesAccess();

  const {
    isSecurityAgentEnabled,
    isAutoscalerEnabled,
    isDiscovered,
    isWoopEnabled,
  } = getOrganizationClusterFeatures(cluster);

  const { palette } = useTheme();

  if (isDiscovered) {
    // suggest anywhere for custom clusters (they have 0 nodes count) or OKE clusters
    const isAnywhere =
      cluster.metrics?.nodesCountTotal?.toNumber() === 0 ||
      cluster.provider === K8sProvider.OKE;
    return (
      <OnboardClusterButton
        disabled={ability.cannot('create', 'Clusters')}
        variant="text"
        size="md"
        initialProvider={isAnywhere ? K8sProvider.ANYWHERE : cluster.provider}
        initialAnywhereClusterName={cluster.name}
      >
        Connect
      </OnboardClusterButton>
    );
  }

  return (
    <Stack
      direction="row"
      sx={{
        gap: 4,
        svg: {
          width: 20,
          height: 20,
          flexShrink: 0,
        },
      }}
      data-testid="features-icons"
    >
      <FeatureIcon title={`${RADAR_NAV_TITLE} is enabled`}>
        <Icons.CastCostMonitoring
          variant="preset"
          color={palette.grey[600]}
          data-testid="cost-monitoring-svg"
        />
      </FeatureIcon>

      {isAutoscalerEnabled && (
        <FeatureIcon title="K8s optimization is enabled">
          <Icons.CastCostOptimization
            variant="preset"
            color={palette.grey[600]}
            data-testid="autoscaler-svg"
          />
        </FeatureIcon>
      )}

      {isSecurityAgentEnabled && (
        <FeatureIcon title="Security features are enabled">
          <Icons.CastSecurity
            variant="preset"
            color={palette.grey[600]}
            data-testid="security-svg"
          />
        </FeatureIcon>
      )}

      {isWoopEnabled && (
        <FeatureIcon title="Workload autoscaler is enabled">
          <Icons.CastWorkloadAutoscaler
            variant="preset"
            color={palette.grey[600]}
            data-testid="woop-svg"
          />
        </FeatureIcon>
      )}
    </Stack>
  );
};
